import { Card, CardBody, Button, Badge, Spinner } from '@components/ui';
import { CreditCardIcon, ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

export function CurrentSubscriptionCard() {
  const { currentPlan, currentSubscription, isLoading } = useSubscription();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <CardBody className="flex items-center justify-center py-8">
          <Spinner size="md" />
        </CardBody>
      </Card>
    );
  }

  const getStatusBadge = () => {
    if (!currentSubscription) {
      return <Badge variant="default">Sin Información</Badge>;
    }

    switch (currentSubscription.status) {
      case 'active':
        return <Badge variant="success">Activo</Badge>;
      case 'trialing':
        return <Badge variant="info">Período de Prueba</Badge>;
      case 'past_due':
        return <Badge variant="warning">Pago Pendiente</Badge>;
      case 'canceled':
        return <Badge variant="danger">Cancelado</Badge>;
      case 'inactive':
        return <Badge variant="default">Inactivo</Badge>;
      default:
        return <Badge variant="default">{currentSubscription.status}</Badge>;
    }
  };

  const planName = currentPlan?.name || 'Sin Suscripción';
  const hasActivePlan = currentSubscription?.status === 'active' || currentSubscription?.status === 'trialing';

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <CreditCardIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                Suscripción Actual
              </h2>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Gestiona tu plan y facturación
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/subscriptions/plans')}
            leftIcon={<ArrowUpCircleIcon className="w-4 h-4" />}
          >
            Ver Planes
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Plan Actual</p>
            <p className="text-lg font-semibold text-secondary-900 dark:text-white">
              {planName}
            </p>
          </div>

          <div>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Estado</p>
            <div>{getStatusBadge()}</div>
          </div>

          <div>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">
              {hasActivePlan ? 'Próxima Renovación' : 'Suscripción'}
            </p>
            <p className="text-lg font-semibold text-secondary-900 dark:text-white">
              {(currentSubscription as any)?.end_date
                ? new Date((currentSubscription as any).end_date).toLocaleDateString('es-PA', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </p>
          </div>
        </div>

        {(currentSubscription as any)?.cancel_at_end && (currentSubscription as any)?.end_date && (
          <div className="mt-4 p-4 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
            <p className="text-sm text-warning-800 dark:text-warning-200">
              Tu suscripción se cancelará al final del período actual. Tendrás acceso hasta el{' '}
              {new Date((currentSubscription as any).end_date).toLocaleDateString('es-PA')}.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
